/**
 * Pre-generated 2-bullet chat introductions for micro lessons
 * Based on Gabi's feedback: Transform narrative context into concise chat openings
 *
 * Format: 2 insight-driven bullet points summarizing the lesson's value
 * Followed by: "Ready to get started learning and applying this to your own work?"
 */

// Standard ending per Gabi's feedback
export const standardChatEnding = `Ready to get started learning and applying this to your own work?`;

export const microSessionChatIntros: Record<string, string> = {
  // FOUNDATIONS TRACK
  'daily-pulse': `Feeling overwhelmed by endless AI possibilities is normal. The problem isn't knowing too little—it's trying to learn everything at once.

Why this matters:
- Small, daily actions build lasting confidence faster than "sprints".
- Identifying one "Daily Win" focuses your energy.

Nonprofit Example:
Distraction: "I need to learn all of ChatGPT today."
Daily Win: "I will use AI to draft *one* email before lunch."

What about you—how are you feeling this morning: Overwhelmed, Focused, or Just Need a Quick Win?`,

  'focus-reset': `When everything feels urgent, nothing is priority. Working harder in chaos just leads to exhaustion, not impact.

Why this matters:
- Strategic clarity is the most valuable asset a leader has.
- A 6-minute pause can save you 4 hours of busywork.

Nonprofit Example:
Chaos: "I have 15 things to do right now."
Focus: "The ONLY thing that matters before noon is the board deck."

What about you—on a scale of 1-10, how scattered do you feel right now?`,

  // COMMUNICATION TRACK
  'prompt-sprint': `Vague prompts produce vague outputs. If you ask AI generic questions, you get generic answers that waste your time.

Why this matters:
- Precision saves you hours of editing and re-prompting.
- Specific prompts put you in control of the output quality.

Nonprofit Example:
Bad: "Write a thank you email."
Good: "Write a 100-word thank you to a first-time donor who gave $50. Tone: Warm and personal."

What about you—what specific writing task is frustrating you right now?`,

  'newsletter-remix': `Rewriting the same update for email, social, and your website triples your workload. You're effectively starting from scratch three times for one piece of news.

Why this matters:
- Repurposing ensures consistent messaging across all channels.
- It frees up your creative energy for new ideas, not rote rewriting.

Nonprofit Example:
Source: Monthly Newsletter Intro.
Outputs: 1 LinkedIn Post, 1 Tweet, and 1 Internal Slack Update.

What about you—what piece of content do you want to repurpose today?`,

  // OPERATIONS TRACK
  'inbox-triage': `A chaotic inbox leads to decision fatigue. When every email requires a fresh response, you drain your mental battery on routine comms.

Why this matters:
- Systems keep your replies kind and consistent, even when you're busy.
- Batching similar emails saves huge amounts of context-switching time.

Nonprofit Example:
Trigger: "I want to volunteer."
Response: A warm, pre-set template asking for their availability and interests.

What about you—what type of email clogs your inbox the most right now?`,

  'ops-playbook-builder': `Right now, that critical process you run? It lives in your head. Which means every time someone needs to do it, they're asking you questions, waiting for your input, or doing it wrong because they're guessing. You've become the bottleneck without meaning to.

**What you'll get:** By the end of our conversation, you'll have a reusable AI prompt that turns any process into a clear, followable operations playbook your team can actually use.

AI can help you build these playbooks—but only if you know how to structure them properly. Most people ask AI to "document my process" and get back vague lists that no one can follow.

Learn this method once, and you'll never struggle to document a process again.

Ready to build your playbook system?`,

  // AUTOMATION TRACK
  'automation-spotlight': `Repetitive tasks are silent time thieves. But automating everything is a trap—you need to know *what* to automate.

Why this matters:
- Automating low-value admin frees you for high-value mission work.
- It prevents errors in boring data entry tasks.

Nonprofit Example:
Task: Copying donor names from emails to a spreadsheet.
Automation: A tool that does this instantly and accurately.

What about you—what repetitive admin task do you dread doing the most?`,

  // LEADERSHIP TRACK
  'signal-scan': `AI headlines are a flood of hype. Most of it is noise that distracts you from the actual work of your mission.

Why this matters:
- Strategic leaders filter noise to focus on what actually helps.
- You avoid wasting resources on "fancy" tools you don't need.

Nonprofit Example:
Noise: "New AI Video Generator!" (Fun but useless for you).
Signal: "AI tool summarizes grant requirements." (High value).

What about you—what recent AI headline or trend is confusing you right now?`,

  'momentum-brief': `Practicing high-stakes conversations is smart, but generic AI roleplays depend on good prompting. I'll help you build a custom "Simulator Prompt" you can use anywhere.
  
  This matters because most people under-prepare for resistance. A good simulator lets you fail safely before it counts.
  
  Ready to build your simulator prompt?`,

  // INSIGHT TRACK
  'research-brief-lab': `Scattered notes don't travel well. You can't just forward a raw transcript to your board and expect them to get it.

Why this matters:
- Synthesized insights drive action; raw data creates confusion.
- A clear brief respects your stakeholders' attention.

Nonprofit Example:
Input: 5 pages of chaotic interview notes.
Output: A 1-page "Key Themes & Quotes" summary.

What about you—what set of notes or research are you trying to synthesize right now?`,

  'impact-reflection': `Moving fast without pausing is dangerous. If you don't reflect, you keep repeating the same inefficient patterns.

Why this matters:
- Reflection turns "experience" into "wisdom."
- It allows you to celebrate wins and course-correct early.

Nonprofit Example:
Reflection: "Our gala raised money but burned out the staff."
Adjustment: "Next year, we hire an event coordinator."

What about you—what recent project or period do you want to reflect on?`,

  // AI FOUNDATIONS TRACK
  'ai-what-why': `AI hype makes it hard to separate genuine capability from marketing promises—without understanding what AI actually does, you can't make confident decisions about where to invest your limited resources.

Clear mental models of AI help you cut through vendor noise and explain your technology choices to skeptical stakeholders who need reasons, not buzzwords.

Ready to get started learning and applying this to your own work?`,

  'ai-choose-tool': `Different AI tools serve different purposes—using ChatGPT for everything is like using a hammer for every home repair, sometimes effective but often frustrating and slow.

Matching the right tool to each task saves time and delivers better results because you're working with AI's strengths instead of fighting its limitations.

Ready to get started learning and applying this to your own work?`,

  'ai-privacy-ethics': `One careless AI interaction could expose sensitive beneficiary information that destroys years of community trust—privacy isn't optional, it's foundational to nonprofit mission integrity.

Practical privacy guardrails protect both your people and your mission while still letting you capture AI's productivity benefits without unnecessary risk.

Ready to get started learning and applying this to your own work?`,

  'raise-framework': `The RAISE framework (Role, Audience, Instructions, Style, Examples) turns vague asks into prompts that work on the first try—no more frustrating cycles of "that's not what I meant."

When prompting becomes systematic instead of guesswork, you spend your energy on strategy instead of wrestling with AI to get basic outputs right.

Ready to get started learning and applying this to your own work?`,

  'prompt-troubleshooting': `When AI output misses the mark, most people just try random changes and hope—systematic troubleshooting identifies exactly what went wrong so you can fix it fast.

Knowing the common failure patterns turns prompt debugging from frustrating guesswork into a quick diagnostic checklist you can run in under a minute.

Ready to get started learning and applying this to your own work?`,

  // CHAPTER 2 LESSONS (converted to micro format)
  'spot-hallucinations': `AI writes with total confidence—even when it's making things up. One fabricated statistic in a donor appeal or grant narrative can tank your credibility faster than you can say "fact-check."

**What you'll learn:** How to spot hallucinations in 30 seconds by recognizing three red flags—specific numbers without sources, vague attributions, and suspiciously perfect examples.

The skill here isn't paranoia—it's pattern recognition. Most hallucinations follow predictable patterns you can learn to catch quickly.

Ready to learn what to look for?`,

  'quality-control': `AI output can be impressive, but without quality control, it's risky. A hallucinated fact or off-brand tone can damage your credibility instantly.

Why this matters:
- You are responsible for everything your organization publishes, regardless of who (or what) wrote it.
- A systematic check catches errors that tired human eyes miss.

Nonprofit Example:
Risk: AI invents a success statistic for a grant report.
QC Check: "Verify source for '90% success rate'."

What about you—what kind of draft do you need to check: A Donor Email, A Social Post, or A Grant Section?`,

  'ch2-l2-rebrief': `When AI output veers off track, starting over wastes the context you've already built—a clear re-brief steers the conversation back faster and produces better results.

Mid-course correction is a skill that separates AI power users from frustrated beginners who abandon promising drafts because they don't know how to redirect.

Ready to get started learning and applying this to your own work?`,

  'ch2-l3-iterate': `Great AI output rarely happens in one shot—the first draft is almost never ready to send, but many people don't know how to systematically improve it.

Deliberate iteration turns "close enough" into "exactly right" through strategic follow-up prompts that refine tone, add specificity, and polish the final product.

Ready to get started learning and applying this to your own work?`,

  // CHAPTER 2 ADDITIONAL LESSONS
  'ai-briefing': `AI doesn't know your organization. It doesn't know your history, your values, your quirks, or the way you actually talk to your community. So every time you ask it to write something, you get output that sounds like every other nonprofit—generic, safe, forgettable.

The problem isn't that you need to edit more. It's that you haven't given AI the context it needs to sound like you in the first place.

**What you'll master:** The iterative voice capture process that builds a reusable AI Brief—a document that teaches any AI tool exactly how your organization communicates. v1 → v2 → v3, with each version getting closer to your authentic voice.

Ready to teach AI to sound like you?`,

  'content-intelligence': `Your organization generates powerful impact data every day—program outcomes, participant testimonials, survey results, activity logs. But this data sits buried in spreadsheets and reports, never reaching the donors and stakeholders who would be moved by it.

The problem isn't that you lack stories. It's that raw data doesn't translate itself into narrative.

**What you'll master:** The iterative refinement process that transforms raw data into compelling narratives. v1 → v2 → v3, with each version becoming more emotionally resonant while staying grounded in evidence.

Ready to turn your data into stories that move people?`,

  'voice-tune-up': `AI drafts sound like everyone else. They're technically correct, maybe even well-written—but they could come from any organization. Your voice—the thing that makes your work recognizable—gets smoothed away into bland corporate-speak.

The problem isn't AI's writing ability. It's that AI defaults to safe, generic patterns that work for everyone but resonate with no one.

**What you'll master:** The three-layer improvement process that transforms any generic draft into content that sounds unmistakably like you. v1 → v2 → v3, with your voice getting stronger at each step.

Ready to turn vanilla copy into something with soul?`,

  'strategic-direction': `Reactive communications feel scattered because there's no thread connecting your messages to mission goals—you're responding to urgency, not strategy.

Why this matters:
- Strategy ensures every email or post serves a specific mission objective.
- It moves you from "posting to post" to "posting to move the needle."

Nonprofit Example:
Wish: "We need more followers."
Strategy: "We will reach 500 local teachers by sharing classroom resources."

What about you—what big goal feels fuzzy right now: Increasing Donations, Recruiting Volunteers, or Raising Awareness?`,

  'communication-systems': `You're rewriting the same emails from scratch every single time. Donor thank-yous, meeting recaps, event reminders—each one feels like starting over because you've never captured what actually makes these communications work.

That's not a writing problem. It's a systems problem.

**What you'll get:** A reusable AI prompt that turns any recurring communication into a template your team can use instantly. No more blank-page paralysis. Just fill in the blanks and send.

The magic isn't in AI's writing—it's in building a system that preserves your voice and handles the structure.

Ready to build your communication system?`,

  // SKILL DEV LESSONS
  'crisis-triage': `When everything feels urgent, nothing gets done well. You're juggling five fires at once—a funder wants an update, a staff member is in crisis, the website is down, a board member is upset, and a partner needs an answer today. Without a system, you either freeze or pick randomly, and one bad prioritization can derail your whole week.

The problem isn't that you have too much to do. It's that you're treating every demand as equally urgent when they're not. Some fires spread. Others burn out on their own. Some look urgent but have zero impact. Others look manageable but will explode if ignored.

**What you'll learn:** How to rank competing demands in 60 seconds using an Impact vs. Urgency framework. This protects your sanity, your team, and your mission from being hijacked by whoever screams loudest.

Ready to learn how to triage like a pro?`,

  // CHAPTER 3 LESSONS
  'analyze-options': `Decisions drag on when you rely on gut feelings. You're staring at three program options, each with compelling pitches and promising outcomes. Without a system, you pick based on who argued loudest, what felt safest, or what sounded most exciting—then spend months wondering if you chose wrong.

The problem isn't that you don't have data. It's that you don't have a way to evaluate competing claims and spot weak evidence. Every option sounds good in the pitch. Some are backed by real evidence. Others are backed by wishful thinking dressed up as data.

**What you'll learn:** How to evaluate program options in 5 minutes by spotting four common evidence traps. This protects your resources, your credibility, and your mission from being swayed by compelling-but-weak arguments.

Ready to learn how to evaluate like a pro?`,

  'build-plans': `Writing a project plan from scratch takes weeks of focus you rarely have. When you rush it, details get missed and funding opportunities slip away.

Why this matters:
- A strong plan aligns your team and secures stakeholder trust.
- AI can generate the "boring but essential" structure (timeline, budget, risks) in minutes.

Nonprofit Example:
Input: "We're launching a community garden in May."
Output: Full 3-phase project plan with milestones, resource list, and risk register.

What about you—what project is stuck in the idea phase: A New Program, A Fundraising Event, or A System Overhaul?`,

  'monitor-finances': `Budget surprises are nightmares. Finding out you're over budget three months in is too late to fix it.

Why this matters:
- Real-time monitoring catches small variances before they become crises.
- It gives you the confidence to answer "Are we on track?" instantly.

Nonprofit Example:
Alert: "Catering spend is trending 15% higher than projected."
Action: Adjust menu for the next event *now* to stay solvent.

What about you—what metric keeps you up at night: Program Budget, Grant Spend-Down, or Staff Hours?`,

  'optimize-workflows': `Your team is working hard, but burnout is looming. You can't see where the time is going, so you can't rebalance the load.

Why this matters:
- Visible workflows let you spot bottlenecks before people quit.
- It protects your team's sustainability and morale.

Nonprofit Example:
Diagnosis: "The Program Manager spends 40% of time on manual data entry."
Fix: Shift entry to a volunteer or automate it.

What about you—where is your team's biggest bottleneck: Administrative Tasks, Communication Overload, or Too Many Meetings?`,

  'automated-oversight': `Chasing status updates is exhausting. You don't want to micromanage, but you can't afford to be in the dark.

Why this matters:
- Automated oversight gives you visibility without nagging your team.
- It frees you to lead strategy instead of just tracking tasks.

Nonprofit Example:
Old Way: Emailing 5 staff members "Is this done?" on Friday.
New Way: A dashboard that auto-updates when tasks are checked off.

What about you—what project needs better visibility: Multiple Grants, Volunteer Coordination, or A Capital Campaign?`,

  // AI FOUNDATIONS - Topic 1: The 4 Core Principles
  'ai-foundations-be-specific': `Vague prompts like "write an email about our program" produce generic, unusable outputs—AI can't read your mind about audience, tone, or purpose.

Precision in prompting saves hours of revision because you're giving AI the specific details it needs to get things right on the first try.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-provide-context': `AI doesn't know your organization, mission, or the people you serve—without context, it gives generic nonprofit advice that could apply to anyone.

The 3-4 critical facts that personalize your prompts transform AI from a generic tool into a strategic thinking partner that understands your work.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-set-format': `AI gives you paragraphs when you needed bullets, or a formal memo when you wanted a casual Slack message—format mismatches waste revision time.

Specifying structure upfront means AI's first draft is closer to your final draft, eliminating frustrating "actually, can you make it..." revision cycles.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-include-examples': `Explaining your desired style in words takes paragraphs and AI still doesn't quite get it—you know what you want but can't articulate it precisely.

One good example replaces five paragraphs of instructions because it captures your tone, structure, and detail level faster than any description can.

Ready to get started learning and applying this to your own work?`,

  // Topic 2: Evaluating & Refining
  'ai-foundations-raise-framework': `You're not sure if AI output is good enough to use—relying on vague gut feeling leads to either over-editing or sending things that shouldn't go out.

The RAISE Framework (Relevance, Accuracy, Impact, Style, Enhancement) gives you a systematic 30-second quality check that catches problems before they become embarrassments.

Ready to get started learning and applying this to your own work?`,

  // Topic 3: Understanding AI
  'ai-foundations-understand-models': `ChatGPT, Claude, Gemini—they all seem the same, leading to tool frustration when you pick the wrong one for your task.

Understanding which AI models excel at writing vs. analysis vs. brainstorming helps you avoid frustration and choose the right tool every time.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-text-image-tools': `You're using text AI for everything, even tasks better suited for image generators, audio tools, or specialized nonprofit applications.

Matching the right tool type to each task turns AI from confusing to clarifying—you'll know exactly which tool to reach for.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-ai-limitations': `You keep asking AI for things it can't do—real-time data, internet searches, perfect accuracy—and getting frustrated when it fails.

Understanding what AI genuinely can't do is liberating because you stop wasting time on impossible asks and focus on what it does brilliantly.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-tool-strategy': `Hundreds of AI tools exist and you don't know which 3-5 actually fit your nonprofit workflow—tool overload leads to decision paralysis.

A focused personal toolkit saves time because 90% coverage with 5 well-chosen tools beats 50% coverage with 20 tools you barely understand.

Ready to get started learning and applying this to your own work?`,

  // Topic 4: AI Ethics and Responsibility
  'ai-foundations-ethical-considerations': `Using AI without ethical guidelines risks harming the communities you serve—your mission requires more care than "move fast and break things."

Thinking through values alignment, transparency, and potential harm strengthens mission integrity instead of compromising it.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-data-privacy': `You're not sure which stakeholder information is safe to use with AI tools—privacy feels like a guessing game with serious consequences if you guess wrong.

Practical safeguards about what data never goes into AI protect everyone you serve while still letting you capture productivity benefits.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-bias-fairness': `AI learned from a biased internet—it might perpetuate exactly the inequities your nonprofit fights against, generating content that alienates the people you serve.

Spotting bias in AI outputs and actively working against systemic problems ensures AI advances equity instead of undermining your mission.

Ready to get started learning and applying this to your own work?`,

  'ai-foundations-ethical-guidelines': `"Can we use AI for that?" gets answered differently by every person on your team—inconsistency creates risk and slows adoption.

A one-page ethical AI policy with shared frameworks and clear guardrails lets your whole team use AI confidently and consistently.

Ready to get started learning and applying this to your own work?`,

  // Topic 5: Setting Up Your AI Toolkit
  'ai-foundations-setup-toolkit': `Your "AI toolkit" is just ChatGPT in a browser tab you keep losing—nothing organized, nothing integrated, no real workflow.

A practical, sustainable AI workspace you'll actually use daily saves time from day one—no complicated integrations or IT expertise required.

Ready to get started learning and applying this to your own work?`,
};

/**
 * Get the chat introduction for a micro session
 * Falls back to a generic intro if specific one not found
 */
export function getChatIntro(sessionId: string): string {
  return microSessionChatIntros[sessionId] || `Learning a new skill works best when you understand why it matters before diving into the how—context makes everything stick better.

We'll walk through this together with practical examples from nonprofit work, then apply it directly to your situation.

${standardChatEnding}`;
}

/**
 * Helper to add "can't think of one?" fallback text
 */
export const exampleFallbackText = "If you can't think of one, I can give you an example.";
